export const DAILY_TASKS_STAGES = [
  "projects",
  "mockup_upload",
  "mockup_approval",
  "file_upload",
  "cdr_to_printer",
  "pr_creation",
  "po_creation",
  "raw_inward",
  "raw_inward_qc",
  "print_outward",
  "print_inward",
  "print_inward_qc",
] as const;

export type DailyTasksStage = (typeof DAILY_TASKS_STAGES)[number];

export const DAILY_TASKS_STAGE_TABS: Array<{
  id: DailyTasksStage;
  label: string;
  enabled: boolean;
}> = [
  { id: "projects", label: "Projects", enabled: true },
  { id: "mockup_upload", label: "Mockup", enabled: true },
  { id: "file_upload", label: "File / CDR", enabled: true },
  { id: "pr_creation", label: "PR creation", enabled: true },
  { id: "po_creation", label: "PO creation", enabled: true },
  { id: "raw_inward", label: "Raw inward", enabled: true },
  { id: "print_outward", label: "Print outward", enabled: true },
  { id: "print_inward", label: "Print inward", enabled: true },
];

export const ENABLED_DAILY_TASKS_STAGES: DailyTasksStage[] =
  DAILY_TASKS_STAGE_TABS.filter((tab) => tab.enabled).map((tab) => tab.id);

const DAILY_TASKS_FAMILY_IDS = new Set(
  DAILY_TASKS_STAGE_TABS.map((tab) => tab.id),
);

export const MERGED_STAGE_ALIASES: Partial<
  Record<DailyTasksStage, DailyTasksStage>
> = {
  mockup_approval: "mockup_upload",
  cdr_to_printer: "file_upload",
  raw_inward_qc: "raw_inward",
  print_inward_qc: "print_inward",
};

export const resolveDailyTasksStage = (
  value?: string | null,
): DailyTasksStage => {
  if (!value) return "mockup_upload";
  const aliased = MERGED_STAGE_ALIASES[value as DailyTasksStage] ?? value;
  if (DAILY_TASKS_FAMILY_IDS.has(aliased as DailyTasksStage)) {
    return aliased as DailyTasksStage;
  }
  return "mockup_upload";
};

export const isDailyTasksFamilyReady = (stage: DailyTasksStage): boolean =>
  ENABLED_DAILY_TASKS_STAGES.includes(stage);

export const dailyTasksFamilyLabel = (stage: DailyTasksStage): string =>
  DAILY_TASKS_STAGE_TABS.find((tab) => tab.id === stage)?.label ?? "Tasks";

export const dailyTasksFamilyBlurb = (stage: DailyTasksStage): string => {
  switch (resolveDailyTasksStage(stage)) {
    case "projects":
      return "How long stock and custom orders take from project created until finance has approved and every product has variants covering ordered qty.";
    case "mockup_upload":
      return "How long custom orders take from created to mockup approval.";
    case "file_upload":
      return "How long custom orders take from mockup approval to sending the file to the printer.";
    case "pr_creation":
      return "How long stock and custom orders take from order created until clean PR quantity covers the ordered quantity (same covering rule as Ops TAT).";
    case "po_creation":
      return "How long stock and custom orders take from project created until every issued purchase order is emailed to the vendor.";
    case "raw_inward":
      return "How long issued POs take from PO created to full inward, then first inward to QC cleared. Same clocks as Ops TAT.";
    case "print_outward":
      return "How long custom products take from raw QC cleared to full print outward. Completion is by sent qty, not stub end_time.";
    case "print_inward":
      return "How long custom products take from full print outward to full print inward, then first inward to print QC cleared. Completion is by received qty and net QC, not stub end_time.";
    default:
      return "This stage will use the same turnaround layout once it is live.";
  }
};

export const INVENTORY_STAGES: DailyTasksStage[] = [
  "raw_inward",
  "raw_inward_qc",
  "print_outward",
  "print_inward",
  "print_inward_qc",
];

export const DAILY_TASKS_SORT_BY = [
  "dispatch_date",
  "start_date",
  "days_to_dispatch",
  "stage_tat_hours",
  "total_tat_hours",
  "mockup_upload_tat_hours",
  "mockup_approval_tat_hours",
  "order_id",
  "brand_name",
  "product_name",
  "pm_poc",
  "sales_poc",
  "mockup_priority",
  "file_priority",
  "pr_priority",
  "po_priority",
  "raw_priority",
  "print_priority",
  "print_in_priority",
  "project_priority",
  "sla_status",
  "mockup_work_state",
] as const;

export type DailyTasksSortBy = (typeof DAILY_TASKS_SORT_BY)[number];

export const MOCKUP_WORK_STATES = [
  "needs_upload",
  "changes_requested",
  "awaiting_approval",
] as const;

export type MockupWorkState = (typeof MOCKUP_WORK_STATES)[number];

export const FILE_WORK_STATES = ["needs_file", "needs_send"] as const;

export type FileWorkState = (typeof FILE_WORK_STATES)[number];

export const PR_WORK_STATES = [
  "needs_pr",
  "partial_pr",
  "pending_approval",
] as const;

export type PrWorkState = (typeof PR_WORK_STATES)[number];

export const PO_WORK_STATES = [
  "needs_po",
  "partial_po",
  "email_to_vendor",
] as const;

export type PoWorkState = (typeof PO_WORK_STATES)[number];

export const RAW_WORK_STATES = [
  "needs_inward",
  "partial_inward",
  "needs_qc",
  "qc_in_progress",
  "qc_on_hold",
] as const;

export type RawWorkState = (typeof RAW_WORK_STATES)[number];

export const PRINT_WORK_STATES = ["needs_outward", "partial_outward"] as const;

export type PrintWorkState = (typeof PRINT_WORK_STATES)[number];

export const PRINT_IN_WORK_STATES = [
  "needs_print_in",
  "partial_print_in",
  "needs_print_qc",
  "print_qc_in_progress",
  "print_qc_on_hold",
] as const;

export type PrintInWorkState = (typeof PRINT_IN_WORK_STATES)[number];

export const PROJECT_WORK_STATES = [
  "needs_request",
  "awaiting_finance",
  "variants_incomplete",
] as const;

export type ProjectWorkState = (typeof PROJECT_WORK_STATES)[number];

export type DailyTasksWorkState =
  | MockupWorkState
  | FileWorkState
  | PrWorkState
  | PoWorkState
  | RawWorkState
  | PrintWorkState
  | PrintInWorkState
  | ProjectWorkState;

export const FILE_WORK_STATE_LABELS: Record<FileWorkState, string> = {
  needs_file: "Needs file",
  needs_send: "Awaiting printer email",
};

export const PR_WORK_STATE_LABELS: Record<PrWorkState, string> = {
  needs_pr: "Needs PR",
  partial_pr: "Partial PR",
  pending_approval: "Approval pending",
};

export const PO_WORK_STATE_LABELS: Record<PoWorkState, string> = {
  needs_po: "Needs PO",
  partial_po: "Partial PO",
  email_to_vendor: "Awaiting email",
};

export const RAW_WORK_STATE_LABELS: Record<RawWorkState, string> = {
  needs_inward: "Awaiting inward",
  partial_inward: "Partial inward",
  needs_qc: "Awaiting QC",
  qc_in_progress: "QC in progress",
  qc_on_hold: "QC on hold",
};

export const PRINT_WORK_STATE_LABELS: Record<PrintWorkState, string> = {
  needs_outward: "Awaiting outward",
  partial_outward: "Partial outward",
};

export const PRINT_IN_WORK_STATE_LABELS: Record<PrintInWorkState, string> = {
  needs_print_in: "Awaiting inward",
  partial_print_in: "Partial inward",
  needs_print_qc: "Awaiting QC",
  print_qc_in_progress: "QC in progress",
  print_qc_on_hold: "QC on hold",
};

export const PROJECT_WORK_STATE_LABELS: Record<ProjectWorkState, string> = {
  needs_request: "No request",
  awaiting_finance: "Awaiting finance",
  variants_incomplete: "Variants incomplete",
};

export const PROJECT_WORK_STATE_OPTIONS = [
  { id: "needs_request", value: "No request" },
  { id: "awaiting_finance", value: "Awaiting finance" },
  { id: "variants_incomplete", value: "Variants incomplete" },
] as const;

export const PRINT_WORK_STATE_OPTIONS = [
  { id: "needs_outward", value: "Awaiting outward" },
  { id: "partial_outward", value: "Partial outward" },
] as const;

export const PRINT_IN_WORK_STATE_OPTIONS = [
  { id: "needs_print_in", value: "Awaiting inward" },
  { id: "partial_print_in", value: "Partial inward" },
  { id: "needs_print_qc", value: "Awaiting QC" },
  { id: "print_qc_in_progress", value: "QC in progress" },
  { id: "print_qc_on_hold", value: "QC on hold" },
] as const;

export const FILE_WORK_STATE_OPTIONS = [
  { id: "needs_file", value: "Needs file" },
  { id: "needs_send", value: "Awaiting printer email" },
] as const;

export const PR_WORK_STATE_OPTIONS = [
  { id: "needs_pr", value: "Needs PR" },
  { id: "partial_pr", value: "Partial PR" },
  { id: "pending_approval", value: "Approval pending" },
] as const;

export const pendingApprovalLabels = (row: {
  pending_sourcing?: boolean | number | null;
  pending_finance?: boolean | number | null;
  pending_swageazy?: boolean | number | null;
}): string[] => {
  const labels: string[] = [];
  if (Number(row.pending_sourcing) === 1 || row.pending_sourcing === true) {
    labels.push("Sourcing");
  }
  if (Number(row.pending_finance) === 1 || row.pending_finance === true) {
    labels.push("Finance");
  }
  if (Number(row.pending_swageazy) === 1 || row.pending_swageazy === true) {
    labels.push("Swageazy");
  }
  return labels;
};

export type VariantsCoverage = "none" | "partial" | "full";

/** Variant SKU qty coverage vs ordered product qty. */
export const variantsCoverage = (
  variantsQty: number | null | undefined,
  orderedQty: number | null | undefined,
): VariantsCoverage => {
  const variants = Number(variantsQty ?? 0);
  const ordered = Number(orderedQty ?? 0);
  if (variants <= 0) return "none";
  if (ordered > 0 && variants < ordered) return "partial";
  return "full";
};

export const VARIANTS_COVERAGE_LABELS: Record<VariantsCoverage, string> = {
  none: "No",
  partial: "Partial",
  full: "Full",
};

/**
 * PR open-work states for chips. Partial and Approval pending can both apply:
 * ordered 100, PR-1 25 fully approved, PR-2 25 waiting → both;
 * both PRs fully approved at 50/100 → Partial only.
 */
export const prOpenWorkStates = (row: {
  pr_qty?: number | null;
  created_pr_qty?: number | null;
  ordered_quantity?: number | null;
  pending_sourcing?: boolean | number | null;
  pending_finance?: boolean | number | null;
  pending_swageazy?: boolean | number | null;
}): PrWorkState[] => {
  const approvedQty = Number(row.pr_qty ?? 0);
  const createdQty = Number(row.created_pr_qty ?? 0);
  const ordered = Number(row.ordered_quantity ?? 0);
  const pending = pendingApprovalLabels(row).length > 0;
  const covering = ordered > 0 && approvedQty >= ordered;
  if (covering) return [];
  if (createdQty <= 0) return ["needs_pr"];
  const states: PrWorkState[] = ["partial_pr"];
  if (pending) states.push("pending_approval");
  return states;
};

export const PO_WORK_STATE_OPTIONS = [
  { id: "needs_po", value: "Needs PO" },
  { id: "partial_po", value: "Partial PO" },
  { id: "email_to_vendor", value: "Awaiting email" },
] as const;

export const RAW_WORK_STATE_OPTIONS = [
  { id: "needs_inward", value: "Awaiting inward" },
  { id: "partial_inward", value: "Partial inward" },
  { id: "needs_qc", value: "Awaiting QC" },
  { id: "qc_in_progress", value: "QC in progress" },
  { id: "qc_on_hold", value: "QC on hold" },
] as const;

export const MOCKUP_WORK_BUCKETS = ["overdue", "today", "all_pending"] as const;

export type MockupWorkBucket = (typeof MOCKUP_WORK_BUCKETS)[number];

export type DailyTasksSlaStatus = "ok" | "warn" | "bad" | null;

export interface MockupWorkSummary {
  overdue: number;
  today: number;
  needs_upload: number;
  needs_upload_today: number;
  needs_upload_overdue: number;
  changes_requested: number;
  changes_requested_today: number;
  changes_requested_overdue: number;
  awaiting_approval: number;
  awaiting_approval_today: number;
  awaiting_approval_overdue: number;
  all_pending: number;
}

export interface FileWorkSummary {
  overdue: number;
  today: number;
  needs_file: number;
  needs_file_today: number;
  needs_file_overdue: number;
  needs_send: number;
  needs_send_today: number;
  needs_send_overdue: number;
  all_pending: number;
}

export const EMPTY_MOCKUP_WORK_SUMMARY: MockupWorkSummary = {
  overdue: 0,
  today: 0,
  needs_upload: 0,
  needs_upload_today: 0,
  needs_upload_overdue: 0,
  changes_requested: 0,
  changes_requested_today: 0,
  changes_requested_overdue: 0,
  awaiting_approval: 0,
  awaiting_approval_today: 0,
  awaiting_approval_overdue: 0,
  all_pending: 0,
};

export const EMPTY_FILE_WORK_SUMMARY: FileWorkSummary = {
  overdue: 0,
  today: 0,
  needs_file: 0,
  needs_file_today: 0,
  needs_file_overdue: 0,
  needs_send: 0,
  needs_send_today: 0,
  needs_send_overdue: 0,
  all_pending: 0,
};

export interface PrWorkSummary {
  overdue: number;
  today: number;
  needs_pr: number;
  needs_pr_today: number;
  needs_pr_overdue: number;
  partial_pr: number;
  partial_pr_today: number;
  partial_pr_overdue: number;
  pending_approval: number;
  pending_approval_today: number;
  pending_approval_overdue: number;
  all_pending: number;
}

export const EMPTY_PR_WORK_SUMMARY: PrWorkSummary = {
  overdue: 0,
  today: 0,
  needs_pr: 0,
  needs_pr_today: 0,
  needs_pr_overdue: 0,
  partial_pr: 0,
  partial_pr_today: 0,
  partial_pr_overdue: 0,
  pending_approval: 0,
  pending_approval_today: 0,
  pending_approval_overdue: 0,
  all_pending: 0,
};

export interface PoWorkSummary {
  overdue: number;
  today: number;
  needs_po: number;
  needs_po_today: number;
  needs_po_overdue: number;
  partial_po: number;
  partial_po_today: number;
  partial_po_overdue: number;
  email_to_vendor: number;
  email_to_vendor_today: number;
  email_to_vendor_overdue: number;
  all_pending: number;
}

export const EMPTY_PO_WORK_SUMMARY: PoWorkSummary = {
  overdue: 0,
  today: 0,
  needs_po: 0,
  needs_po_today: 0,
  needs_po_overdue: 0,
  partial_po: 0,
  partial_po_today: 0,
  partial_po_overdue: 0,
  email_to_vendor: 0,
  email_to_vendor_today: 0,
  email_to_vendor_overdue: 0,
  all_pending: 0,
};

export interface RawWorkSummary {
  overdue: number;
  today: number;
  needs_inward: number;
  needs_inward_today: number;
  needs_inward_overdue: number;
  partial_inward: number;
  partial_inward_today: number;
  partial_inward_overdue: number;
  needs_qc: number;
  needs_qc_today: number;
  needs_qc_overdue: number;
  qc_in_progress: number;
  qc_in_progress_today: number;
  qc_in_progress_overdue: number;
  qc_on_hold: number;
  qc_on_hold_today: number;
  qc_on_hold_overdue: number;
  all_pending: number;
}

export const EMPTY_RAW_WORK_SUMMARY: RawWorkSummary = {
  overdue: 0,
  today: 0,
  needs_inward: 0,
  needs_inward_today: 0,
  needs_inward_overdue: 0,
  partial_inward: 0,
  partial_inward_today: 0,
  partial_inward_overdue: 0,
  needs_qc: 0,
  needs_qc_today: 0,
  needs_qc_overdue: 0,
  qc_in_progress: 0,
  qc_in_progress_today: 0,
  qc_in_progress_overdue: 0,
  qc_on_hold: 0,
  qc_on_hold_today: 0,
  qc_on_hold_overdue: 0,
  all_pending: 0,
};

export interface PrintWorkSummary {
  overdue: number;
  today: number;
  needs_outward: number;
  needs_outward_today: number;
  needs_outward_overdue: number;
  partial_outward: number;
  partial_outward_today: number;
  partial_outward_overdue: number;
  all_pending: number;
}

export const EMPTY_PRINT_WORK_SUMMARY: PrintWorkSummary = {
  overdue: 0,
  today: 0,
  needs_outward: 0,
  needs_outward_today: 0,
  needs_outward_overdue: 0,
  partial_outward: 0,
  partial_outward_today: 0,
  partial_outward_overdue: 0,
  all_pending: 0,
};

export interface PrintInWorkSummary {
  overdue: number;
  today: number;
  needs_print_in: number;
  needs_print_in_today: number;
  needs_print_in_overdue: number;
  partial_print_in: number;
  partial_print_in_today: number;
  partial_print_in_overdue: number;
  needs_print_qc: number;
  needs_print_qc_today: number;
  needs_print_qc_overdue: number;
  print_qc_in_progress: number;
  print_qc_in_progress_today: number;
  print_qc_in_progress_overdue: number;
  print_qc_on_hold: number;
  print_qc_on_hold_today: number;
  print_qc_on_hold_overdue: number;
  all_pending: number;
}

export const EMPTY_PRINT_IN_WORK_SUMMARY: PrintInWorkSummary = {
  overdue: 0,
  today: 0,
  needs_print_in: 0,
  needs_print_in_today: 0,
  needs_print_in_overdue: 0,
  partial_print_in: 0,
  partial_print_in_today: 0,
  partial_print_in_overdue: 0,
  needs_print_qc: 0,
  needs_print_qc_today: 0,
  needs_print_qc_overdue: 0,
  print_qc_in_progress: 0,
  print_qc_in_progress_today: 0,
  print_qc_in_progress_overdue: 0,
  print_qc_on_hold: 0,
  print_qc_on_hold_today: 0,
  print_qc_on_hold_overdue: 0,
  all_pending: 0,
};

export interface ProjectWorkSummary {
  overdue: number;
  today: number;
  needs_request: number;
  needs_request_today: number;
  needs_request_overdue: number;
  awaiting_finance: number;
  awaiting_finance_today: number;
  awaiting_finance_overdue: number;
  variants_incomplete: number;
  variants_incomplete_today: number;
  variants_incomplete_overdue: number;
  all_pending: number;
}

export const EMPTY_PROJECT_WORK_SUMMARY: ProjectWorkSummary = {
  overdue: 0,
  today: 0,
  needs_request: 0,
  needs_request_today: 0,
  needs_request_overdue: 0,
  awaiting_finance: 0,
  awaiting_finance_today: 0,
  awaiting_finance_overdue: 0,
  variants_incomplete: 0,
  variants_incomplete_today: 0,
  variants_incomplete_overdue: 0,
  all_pending: 0,
};

export type MockupWorkUrgency = "any" | "today" | "overdue";

export const MOCKUP_WORK_STATE_LABELS: Record<MockupWorkState, string> = {
  needs_upload: "Needs upload",
  changes_requested: "Rework",
  awaiting_approval: "Awaiting approval",
};

export const MOCKUP_WORK_STATE_OPTIONS = [
  { id: "needs_upload", value: "Needs upload" },
  { id: "changes_requested", value: "Rework" },
  { id: "awaiting_approval", value: "Awaiting approval" },
] as const;

export const MOCKUP_WORK_BUCKET_OPTIONS = [
  { id: "overdue", value: "Overdue" },
  { id: "today", value: "Today" },
  { id: "all_pending", value: "All pending" },
] as const;

export const DEFAULT_MOCKUP_WORK_BUCKET = [
  { id: "overdue", value: "Overdue" },
  { id: "today", value: "Today" },
] as const;

export const DEFAULT_INVENTORY_DISPATCH_BUCKET = [
  { id: "overdue_or_today", value: "Overdue / today" },
] as const;

export interface DailyTasksFilterOption {
  id: number | string;
  value: string;
}

export interface DailyTasksFilterOptions {
  order_types: DailyTasksFilterOption[];
  sales_pocs: DailyTasksFilterOption[];
  pm_pocs: DailyTasksFilterOption[];
  warehouses: DailyTasksFilterOption[];
}

export interface DailyTasksStageSummaryItem {
  stage: DailyTasksStage;
  label: string;
  count: number;
}

export interface DailyTasksListRow {
  order_id: number;
  order_type_id: number;
  order_type: string | null;
  brand_name: string | null;
  start_date: string | Date | null;
  dispatch_date: string | Date | null;
  product_id: number;
  product_name: string | null;
  ordered_quantity: number;
  customisation_required: number;
  sales_poc_id: number | null;
  sales_poc: string | null;
  pm_poc_id: number | null;
  pm_poc: string | null;
  warehouse_id: string | null;
  days_to_dispatch: number | null;
  days_to_sla: number | null;
  stage_tat_hours: number | null;
  total_tat_hours: number | null;
  stage_status: "pending" | "completed";
  mockup_count: number;
  last_uploaded_at: string | Date | null;
  last_uploaded_by: string | null;
  logos_uploaded: boolean;
  variants_qty: number;
  mockup_work_state: DailyTasksWorkState | null;
  file_upload_sla_days: number | null;
  cdr_to_printer_sla_days: number | null;
  file_upload_tat_hours: number | null;
  cdr_to_printer_tat_hours: number | null;
  pr_creation_sla_days: number | null;
  pr_creation_tat_hours: number | null;
  po_creation_sla_days: number | null;
  po_creation_tat_hours: number | null;
  raw_inward_sla_days: number | null;
  raw_inward_qc_sla_days: number | null;
  print_outward_sla_days: number | null;
  print_inward_sla_days: number | null;
  print_inward_qc_sla_days: number | null;
  project_request_sla_days: number | null;
  project_variants_sla_days: number | null;
  project_invoice_sla_days: number | null;
  customer_invoice_present: boolean;
  customer_invoice_date: string | Date | null;
  pr_qty: number;
  created_pr_qty: number;
  pending_sourcing: boolean;
  pending_finance: boolean;
  pending_swageazy: boolean;
  pr_vendor_name: string | null;
  first_pr_at: string | Date | null;
  covering_pr_at: string | Date | null;
  approved_pr_count: number;
  issued_po_count: number;
  sent_po_count: number;
  first_po_at: string | Date | null;
  all_po_at: string | Date | null;
  all_email_at: string | Date | null;
  printer_name: string | null;
  printing_type: string | null;
  printing_cost: number | null;
  mockups_approved_at: string | Date | null;
  sla_clock_start: string | Date | null;
  sla_target_hours: number | null;
  sla_elapsed_hours: number | null;
  sla_status: DailyTasksSlaStatus;
  sla_delta_hours: number | null;
  mockup_upload_sla_days: number | null;
  mockup_approval_sla_days: number | null;
  mockup_rework_sla_days: number | null;
  mockup_upload_tat_hours: number | null;
  mockup_approval_tat_hours: number | null;
  file_count: number;
  last_file_at: string | Date | null;
  po_count: number;
  received_quantity: number | null;
  rejected_quantity: number | null;
  sent_quantity: number | null;
  ordered_po_quantity: number | null;
  /** Distinct expected_delivery_date values (YYYY-MM-DD) from clean PRs with an issued PO. */
  expected_inward_dates?: string[] | null;
  /** Distinct print_outward.timeline values (YYYY-MM-DD) — expected print-in dates. */
  print_inward_timelines?: string[] | null;
  project_finance_status: "approved" | "rejected" | "pending" | null;
  project_finance_approval_date: string | Date | null;
  project_finance_tat_hours: number | null;
  commander_path: string;
  /** Active (non-deleted) product-level comments from sourcing ops. */
  comments_count: number;
}

export interface DailyTasksPaginatedList {
  page_no: number;
  total_pages: number;
  total_count: number;
  limit: number;
  list: DailyTasksListRow[];
}

export interface MockupCommentDto {
  id: number;
  mockup_id: number;
  comment: string | null;
  admin_name: string | null;
  user_name: string | null;
  created_date: string | Date | null;
}

export interface ProductCommentAttachmentDrawerDto {
  id: number;
  file_path: string;
  file_name: string | null;
}

/** Product-level comments from sourcing ops (`projects.product_comments`). */
export interface ProductCommentDrawerDto {
  id: number;
  parent_id: number | null;
  body: string;
  created_by: number;
  author_name: string | null;
  created_at: string | Date | null;
  updated_at: string | Date | null;
  deleted_at: string | Date | null;
  attachments: ProductCommentAttachmentDrawerDto[];
  replies: ProductCommentDrawerDto[];
}

export interface MockupDetailDto {
  id: number;
  image_url: string;
  approved: number | boolean;
  approver_name: string | null;
  approved_date: string | Date | null;
  changes_requested: number | boolean;
  changes_requested_by_name: string | null;
  changes_requested_date: string | Date | null;
  is_rejected: number | boolean | null;
  rejection_note: string | null;
  rejected_date: string | Date | null;
  uploaded_by: string | null;
  created_date: string | Date | null;
  image_url_change_count?: number | null;
  image_url_last_changed_at?: string | Date | null;
  comments: MockupCommentDto[];
}

export type MockupIterationStatus =
  | "pending"
  | "changes_requested"
  | "rejected"
  | "approved";

export const MOCKUP_ITERATION_STATUS_LABELS: Record<
  MockupIterationStatus,
  string
> = {
  pending: "Pending",
  changes_requested: "Rework",
  rejected: "Rejected",
  approved: "Approved",
};

export interface MockupIterationDto {
  iteration: number;
  status: MockupIterationStatus;
  started_at: string | Date | null;
  closed_at: string | Date | null;
  mockups: MockupDetailDto[];
}

const flagOn = (value: number | boolean | string | null | undefined): boolean =>
  value === true || value === 1 || value === "1";

const toTimestamp = (
  value: string | Date | null | undefined,
): number | null => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
};

export const mockupDecisionStatus = (
  mockup: MockupDetailDto,
): MockupIterationStatus => {
  if (flagOn(mockup.approved)) return "approved";
  if (flagOn(mockup.changes_requested)) return "changes_requested";
  if (flagOn(mockup.is_rejected)) return "rejected";
  return "pending";
};

const decisionAt = (mockup: MockupDetailDto): number | null => {
  const status = mockupDecisionStatus(mockup);
  if (status === "approved") return toTimestamp(mockup.approved_date);
  if (status === "changes_requested") {
    return toTimestamp(mockup.changes_requested_date);
  }
  if (status === "rejected") return toTimestamp(mockup.rejected_date);
  return null;
};

const sortMockupsByUpload = (mockups: MockupDetailDto[]): MockupDetailDto[] =>
  [...mockups].sort((left, right) => {
    const leftTime = toTimestamp(left.created_date) ?? 0;
    const rightTime = toTimestamp(right.created_date) ?? 0;
    if (leftTime !== rightTime) return leftTime - rightTime;
    return left.id - right.id;
  });

const toIteration = (
  mockups: MockupDetailDto[],
  iteration: number,
): MockupIterationDto | null => {
  const latest = mockups[mockups.length - 1];
  const first = mockups[0];
  if (!latest || !first) return null;
  const status = mockupDecisionStatus(latest);
  const closedAt =
    status === "approved"
      ? latest.approved_date
      : status === "changes_requested"
        ? latest.changes_requested_date
        : status === "rejected"
          ? latest.rejected_date
          : null;
  return {
    iteration,
    status,
    started_at: first.created_date,
    closed_at: closedAt,
    mockups,
  };
};

const groupMockupRounds = (mockups: MockupDetailDto[]): MockupDetailDto[][] => {
  const sorted = sortMockupsByUpload(mockups);
  const rounds: MockupDetailDto[][] = [];
  let bucket: MockupDetailDto[] = [];

  const flush = () => {
    if (bucket.length === 0) return;
    rounds.push(bucket);
    bucket = [];
  };

  for (const mockup of sorted) {
    if (bucket.length === 0) {
      bucket.push(mockup);
      continue;
    }
    const previous = bucket[bucket.length - 1];
    const previousStatus = mockupDecisionStatus(previous);
    const previousClosedAt = decisionAt(previous);
    const uploadedAt = toTimestamp(mockup.created_date);
    const startsNewRound =
      previousStatus !== "pending" &&
      (previousClosedAt == null ||
        uploadedAt == null ||
        uploadedAt > previousClosedAt);

    if (startsNewRound) {
      flush();
    }
    bucket.push(mockup);
  }
  flush();
  return rounds;
};

export const currentMockupRound = (
  mockups: MockupDetailDto[],
): MockupDetailDto[] => {
  try {
    const rounds = groupMockupRounds(mockups);
    return rounds.length ? rounds[rounds.length - 1] : [];
  } catch (error) {
    console.error("Failed to resolve current mockups:", error);
    return mockups;
  }
};

export const groupMockupIterations = (
  mockups: MockupDetailDto[],
): MockupIterationDto[] => {
  try {
    const supersededRounds = groupMockupRounds(mockups).slice(0, -1);
    return supersededRounds
      .map((bucket, index) => toIteration(bucket, index + 1))
      .filter((round): round is MockupIterationDto => round != null);
  } catch (error) {
    console.error("Failed to group mockup iterations:", error);
    return [];
  }
};

export interface PrintingFileDto {
  id: number;
  image_url: string;
  admin_name: string | null;
  sent_to_printer: number | boolean | null;
  sent_to_printer_date: string | Date | null;
  created_date: string | Date | null;
}

export interface PipelineStepDto {
  stage: string;
  task: string;
  complete: boolean;
  completed_at: string | Date | null;
  completed_by: string | null;
}

export interface SkuLineDto {
  sku: string;
  title: string | null;
  ordered: number;
  received: number;
  rejected: number;
  sent: number;
  pending: number;
}

export type ClientLogoUploadedBy = "client" | "admin" | "unknown";
export type ClientLogoScope = "product" | "order";

export interface ClientLogoDto {
  id: number;
  image_url: string;
  created_date: string | Date | null;
  uploaded_by: ClientLogoUploadedBy;
  scope: ClientLogoScope;
}

export interface PurchaseOrderDrawerDto {
  po_id: number;
  pr_id: number | null;
  po_number: string | null;
  vendor_name: string | null;
  warehouse_id: string | null;
  created_date: string | Date | null;
  created_by: string | null;
  status: string | null;
  email_sent: boolean;
  email_sent_date: string | Date | null;
  totals: {
    ordered: number;
    received: number;
    rejected: number;
    sent: number;
    pending: number;
  };
  events: {
    first_at: string | Date | null;
    last_at: string | Date | null;
    by_admin: string | null;
    expected_at: string | Date | null;
  };
  qc_in_progress: boolean;
  qc_on_hold: boolean;
  hold_reason: string | null;
  rejection_reason: string | null;
  invoice_received: boolean;
  printer_name: string | null;
  timeline: string | Date | null;
  sku_lines: SkuLineDto[];
}

export interface PurchaseRequestDrawerDto {
  id: number;
  vendor_name: string | null;
  quantity: number;
  created_date: string | Date | null;
  created_by: string | null;
  expected_delivery_date: string | Date | null;
  sourcing_approval_date: string | Date | null;
  sourcing_approved_by: string | null;
  finance_approval_date: string | Date | null;
  finance_approved_by: string | null;
  swageazy_approval_date: string | Date | null;
  swageazy_approved_by: string | null;
  rejection_date_by_sourcing: string | Date | null;
  sourcing_rejected_by: string | null;
  rejection_date_by_finance: string | Date | null;
  finance_rejected_by: string | null;
  rejection_date_by_swageazy: string | Date | null;
  swageazy_rejected_by: string | null;
  pr_cancellation_date: string | Date | null;
  cancelled_by: string | null;
  is_clean: boolean;
  is_fully_approved: boolean;
  swageazy_approval_required: boolean;
}

export interface DailyTasksDetailResult {
  order_id: number;
  product_id: number;
  brand_name: string | null;
  product_name: string | null;
  order_type: string | null;
  start_date: string | Date | null;
  dispatch_date: string | Date | null;
  ordered_quantity: number;
  customisation_required: boolean;
  sales_poc: string | null;
  pm_poc: string | null;
  days_to_dispatch: number | null;
  days_to_sla: number | null;
  stage_tat_hours: number | null;
  total_tat_hours: number | null;
  mockup_work_state: DailyTasksWorkState | null;
  file_upload_sla_days: number | null;
  cdr_to_printer_sla_days: number | null;
  file_upload_tat_hours: number | null;
  cdr_to_printer_tat_hours: number | null;
  pr_creation_sla_days: number | null;
  pr_creation_tat_hours: number | null;
  po_creation_sla_days: number | null;
  po_creation_tat_hours: number | null;
  raw_inward_sla_days: number | null;
  raw_inward_qc_sla_days: number | null;
  print_outward_sla_days: number | null;
  print_inward_sla_days: number | null;
  print_inward_qc_sla_days: number | null;
  project_request_sla_days: number | null;
  project_variants_sla_days: number | null;
  project_invoice_sla_days: number | null;
  customer_invoice_present: boolean;
  customer_invoice_date: string | Date | null;
  sent_quantity: number | null;
  pr_qty: number;
  created_pr_qty: number;
  pending_sourcing: boolean;
  pending_finance: boolean;
  pending_swageazy: boolean;
  pr_vendor_name: string | null;
  first_pr_at: string | Date | null;
  covering_pr_at: string | Date | null;
  approved_pr_count: number;
  issued_po_count: number;
  sent_po_count: number;
  first_po_at: string | Date | null;
  all_po_at: string | Date | null;
  all_email_at: string | Date | null;
  printer_name: string | null;
  printing_type: string | null;
  printing_cost: number | null;
  mockups_approved_at: string | Date | null;
  sla_clock_start: string | Date | null;
  sla_target_hours: number | null;
  sla_elapsed_hours: number | null;
  sla_status: DailyTasksSlaStatus;
  sla_delta_hours: number | null;
  mockup_upload_sla_days: number | null;
  mockup_approval_sla_days: number | null;
  mockup_rework_sla_days: number | null;
  mockup_upload_tat_hours: number | null;
  mockup_approval_tat_hours: number | null;
  project_finance_status: "approved" | "rejected" | "pending" | null;
  project_finance_approval_date: string | Date | null;
  project_finance_tat_hours: number | null;
  variants_qty: number;
  comments_count: number;
  commander_path: string;
  pipeline: PipelineStepDto[];
  mockups: MockupDetailDto[];
  iterations?: MockupIterationDto[];
  logos: ClientLogoDto[];
  printing_files: PrintingFileDto[];
  purchase_orders: PurchaseOrderDrawerDto[];
  purchase_requests: PurchaseRequestDrawerDto[];
  product_comments: ProductCommentDrawerDto[];
}

export const DATE_FIELD_OPTIONS = [
  { id: "dispatch_date", value: "Dispatch date" },
  { id: "start_date", value: "Start date" },
  { id: "stage_completed", value: "Stage completed" },
] as const;

/** Raw / Print inward — cohort date for the range filter. */
export const INWARD_DATE_FIELD_OPTIONS = [
  { id: "start_date", value: "Project approved" },
  { id: "expected_inward_date", value: "Expected inward" },
  { id: "print_inward_timeline", value: "Print inward timeline" },
] as const;

export const DISPATCH_BUCKET_OPTIONS = [
  { id: "overdue_or_today", value: "Overdue / today" },
  { id: "next_3_days", value: "Next 3 days" },
  { id: "days_4_to_7", value: "4–7 days" },
  { id: "days_7_plus", value: "7+ days" },
] as const;

export const YES_NO_ALL_OPTIONS = [
  { id: "all", value: "All" },
  { id: "yes", value: "Yes" },
  { id: "no", value: "No" },
] as const;

export const CDR_FIELD_OPTIONS = [
  { id: "all", value: "All" },
  { id: "missing", value: "Missing" },
  { id: "added", value: "Added" },
] as const;
